import styled from "styled-components";
import { getIQContentImage } from "../../utils/utility-functions";

const getIQGridCols = (count) => (count === 4 ? 2 : Math.min(count, 3));

const IQQuestion = ({
  question,
  currentIndex,
  totalQuestions,
  userAnswers,
  isQuizComplete,
  isProcessing,
  currentLocale,
  onSelectAnswer,
  onNext,
  onBack,
}) => {
  const sortedOpts = question.questionOption || [];
  const hasImgs = sortedOpts.some((o) => o.mediaUrl);
  const selected = userAnswers[question.id];
  const isLast = currentIndex === totalQuestions - 1;

  return (
    <IQQuestionWrapper>
      <IQCounter>
        {currentIndex + 1} / {totalQuestions}
      </IQCounter>

      <IQQuestionText>
        {question.title_EN ? question.title_EN.split(/[|\n]/)[0].trim() : ""}
      </IQQuestionText>

      {question.mediaUrl ? (
        <IQQuestionImageCard>
          <IQQuestionImage
            src={getIQContentImage(
              question.mediaUrl,
              "questions",
              currentLocale,
            )}
            alt=""
          />
        </IQQuestionImageCard>
      ) : question.title_EN && question.title_EN.includes("|") ? (
        <IQQuestionImageCard isContext>
          <IQQuestionContextText>
            {question.title_EN.split("|")[1].trim()}
          </IQQuestionContextText>
        </IQQuestionImageCard>
      ) : null}

      {hasImgs ? (
        <IQImageOptionsGrid cols={getIQGridCols(sortedOpts.length)}>
          {sortedOpts.map((opt, i) => (
            <IQImageOptionCard
              key={opt.id}
              $isSelected={selected === opt.id}
              type="button"
              onClick={() =>
                !isQuizComplete && onSelectAnswer(question.id, opt.id)
              }
            >
              <IQOptionBadge $isSelected={selected === opt.id}>
                {String.fromCharCode(65 + i)}
              </IQOptionBadge>
              <IQOptionImageWrapper>
                <img
                  src={getIQContentImage(
                    opt.mediaUrl,
                    "answers",
                    currentLocale,
                  )}
                  alt={opt.label || ""}
                />
              </IQOptionImageWrapper>
            </IQImageOptionCard>
          ))}
        </IQImageOptionsGrid>
      ) : (
        <IQTextOptionsContainer>
          {sortedOpts.map((opt, i) => (
            <IQTextOptionButton
              key={opt.id}
              $isSelected={selected === opt.id}
              type="button"
              onClick={() =>
                !isQuizComplete && onSelectAnswer(question.id, opt.id)
              }
            >
              <IQOptionBadge $isSelected={selected === opt.id}>
                {String.fromCharCode(65 + i)}
              </IQOptionBadge>
              <span>{opt.label}</span>
            </IQTextOptionButton>
          ))}
        </IQTextOptionsContainer>
      )}

      <IQNavBar>
        <IQBackBtn type="button" onClick={onBack} disabled={currentIndex === 0}>
          ← Back
        </IQBackBtn>
        <IQNextBtn
          type="button"
          onClick={onNext}
          disabled={!selected || isProcessing}
        >
          {isLast ? "Finish" : "Next →"}
        </IQNextBtn>
      </IQNavBar>
    </IQQuestionWrapper>
  );
};

export default IQQuestion;

const IQQuestionWrapper = styled.div`
  max-width: 660px;
  width: 100%;
  margin: 0 auto;
  padding: 28px 16px 40px;
`;

const IQCounter = styled.p`
  font-size: 0.85rem;
  color: #6b7a99;
  margin-bottom: 12px;
`;

const IQQuestionText = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111318;
  line-height: 1.45;
  margin-bottom: 20px;
`;

const IQQuestionImageCard = styled.div`
  background: #ffffff;
  border: ${({ isContext }) => (isContext ? "1px solid #d6dae8" : "none")};
  border-radius: ${({ isContext }) => (isContext ? "8px" : "16px")};
  padding: ${({ isContext }) => (isContext ? "14px 20px" : "24px 20px")};
  margin: ${({ isContext }) => (isContext ? "0 0 20px" : "0 auto 20px")};
  display: flex;
  align-items: center;
  justify-content: ${({ isContext }) => (isContext ? "flex-start" : "center")};
  width: 100%;
  max-width: ${({ isContext }) => (isContext ? "100%" : "540px")};
  min-height: ${({ isContext }) => (isContext ? "auto" : "180px")};
  box-shadow: ${({ isContext }) =>
    isContext ? "none" : "0 1px 6px rgba(0,0,0,.06)"};
`;

const IQQuestionImage = styled.img`
  object-fit: contain;
`;

const IQQuestionContextText = styled.p`
  font-size: 1rem;
  color: #111318;
  line-height: 1.6;
  text-align: left;
  font-weight: 500;
  white-space: pre-line;
`;

const IQTextOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const IQTextOptionButton = styled.button`
  width: 100%;
  background: ${({ $isSelected }) => ($isSelected ? "#EAF0FF" : "#ffffff")};
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? "#1D3FA3" : "#D6DAE8")};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  text-align: left;
  font-size: 0.95rem;
  color: #111318;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    background 0.15s;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? "0 0 0 4px rgba(29,63,163,.16)" : "none"};

  &:hover {
    border-color: #1d3fa3;
  }
`;

const IQImageOptionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 auto 24px;
  width: 100%;
  max-width: ${({ cols }) => (cols < 3 ? "480px" : "unset")};
  justify-content: center;

  & > button {
    flex: 0 0
      ${({ cols }) => (cols >= 3 ? "calc(33.33% - 7px)" : "calc(50% - 5px)")};
    min-width: 0;
  }
`;

const IQImageOptionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${({ $isSelected }) => ($isSelected ? "#EAF0FF" : "#ffffff")};
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? "#1D3FA3" : "#D6DAE8")};
  border-radius: 12px;
  padding: 8px 8px 12px;
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    background 0.15s;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? "0 0 0 4px rgba(29,63,163,.16)" : "none"};

  &:hover {
    border-color: #1d3fa3;
  }
`;

const IQOptionImageWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 4px 0;

  img {
    object-fit: contain;
  }
`;

const IQOptionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: monospace;
  color: ${({ $isSelected }) => ($isSelected ? "#1D3FA3" : "#3A4A6A")};
  flex-shrink: 0;
`;

const IQNavBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 12px;
`;

const IQBackBtn = styled.button`
  border: 2px solid #d6dae8;
  background: transparent;
  color: #111318;
  border-radius: 8px;
  padding: 10px 28px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: #1d3fa3;
  }
`;

const IQNextBtn = styled.button`
  background: ${({ disabled }) => (disabled ? "#8FA5D8" : "#1D3FA3")};
  border: 2px solid ${({ disabled }) => (disabled ? "#8FA5D8" : "#1D3FA3")};
  color: #fff;
  border-radius: 8px;
  padding: 10px 28px;
  font-size: 0.95rem;
  font-weight: 600;
  min-width: 120px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover:not(:disabled) {
    background: #1530a0;
    border-color: #1530a0;
  }
`;
